import React from 'react';
import { TrendingUp, Target, CheckCircle2 } from 'lucide-react';

const CaseStudyCard = ({ study, index }) => {
  const isReverse = index % 2 === 1;

  return (
    <article
      data-testid={`case-study-${study.slug}`}
      className="relative bg-white border border-blue-100 rounded-3xl shadow-sm hover:shadow-xl transition-shadow duration-500 overflow-hidden"
    >
      {/* Accent strip */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600" />

      <div className="p-8 sm:p-10 lg:p-14">
        {/* Header: logo + meta */}
        <div
          className={`flex flex-col ${
            isReverse ? 'lg:flex-row-reverse' : 'lg:flex-row'
          } lg:items-center gap-8 mb-10`}
        >
          <div className="flex-shrink-0 w-64 h-32 bg-white border border-blue-100 rounded-2xl flex items-center justify-center p-2 shadow-sm">
            <img
              src={study.logo}
              alt={`${study.client} logo`}
              className="max-h-full max-w-full object-contain"
            />
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wider">
                {study.category}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                {study.duration}
              </span>
              {study.budget && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                  Budget: {study.budget}
                </span>
              )}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
              {study.title}
            </h2>
            <p className="text-gray-500 text-sm">
              {study.client} · {study.role}
            </p>
          </div>
        </div>

        {/* Results metrics grid - the star of the card */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Results</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {study.results.map((r, i) => (
              <div
                key={i}
                data-testid={`${study.slug}-metric-${i}`}
                className="group relative bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-2xl p-5 hover:border-blue-300 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="text-2xl sm:text-3xl font-bold text-blue-600 leading-none">
                  {r.value}
                </div>
                <div className="mt-2 text-xs sm:text-sm text-gray-600 leading-snug">
                  {r.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Challenge + Approach: two-column narrative */}
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">The Challenge</h3>
            </div>
            <p className="text-gray-600 leading-relaxed">{study.challenge}</p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Our Approach</h3>
            </div>
            <p className="text-gray-600 leading-relaxed">{study.approach}</p>
          </div>
        </div>

        {/* Key takeaway pull-quote */}
        {study.takeaway && (
          <div className="mb-10 border-l-4 border-blue-500 bg-blue-50/60 rounded-r-2xl p-6">
            <p className="text-gray-800 text-base sm:text-lg italic leading-relaxed">
              “{study.takeaway}”
            </p>
            <p className="mt-2 text-sm text-blue-700 font-semibold">
              Key Takeaway
            </p>
          </div>
        )}

        {/* Services pills */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Services Used
          </h3>
          <div className="flex flex-wrap gap-2">
            {study.services.map((s, i) => (
              <span
                key={i}
                className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 text-sm rounded-full hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-colors cursor-default"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
};

export default CaseStudyCard;
